import { Tooltip } from "@shopify/polaris"
import React, { useContext, useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import SlotCounter from "react-slot-counter"
import KopyLogo from "../../assets/kopy-logo-square.png"
import { useMediaQuery } from "../../hooks/useMediaQuery.js"
import {
	useCalculateItemCost,
	useFeatureFlagsReady,
} from "../../hooks/usePricingHooks.js"
import { CoolButton } from "../CoolButton.jsx"
import { GlobalStateContext } from "../providers/GlobalStateProvider.jsx"
import { Video } from "../Video.jsx"
import { roundPrice } from "../PriceRounding.jsx"

const KOPY_PAGES_TOOLTIP_VIDEO_URL =
	"https://cdn.prod.website-files.com/65369b1cd95b8d235a768f9b%2F69c531bf2624974b8ed1e6ef_kopy-pages-2.0_mp4.mp4"

const CheckmarkIcon = (
	<svg
		style={{
			display: "block",
			height: "1.15rem",
			width: "1.15rem",
			// color: "#09c709",
			color: "#5371FF",
		}}
		viewBox="0 0 24 24"
	>
		<g fill="none" fillRule="evenodd">
			<path d="M24 0v24H0V0zM12.594 23.258l-.012.002l-.071.035l-.02.004l-.014-.004l-.071-.036c-.01-.003-.019 0-.024.006l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427c-.002-.01-.009-.017-.016-.018m.264-.113l-.014.002l-.184.093l-.01.01l-.003.011l.018.43l.005.012l.008.008l.201.092c.012.004.023 0 .029-.008l.004-.014l-.034-.614c-.003-.012-.01-.02-.02-.022m-.715.002a.023.023 0 0 0-.027.006l-.006.014l-.034.614c0 .012.007.02.017.024l.015-.002l.201-.093l.01-.008l.003-.011l.018-.43l-.003-.012l-.01-.01z"></path>
			<path
				fill="currentColor"
				d="M19.495 3.133a1 1 0 0 1 1.352.309l.99 1.51a1 1 0 0 1-.155 1.279l-.003.004l-.014.013l-.057.053l-.225.215a83.86 83.86 0 0 0-3.62 3.736c-2.197 2.416-4.806 5.578-6.562 8.646c-.49.856-1.687 1.04-2.397.301l-6.485-6.738a1 1 0 0 1 .051-1.436l1.96-1.768A1 1 0 0 1 5.6 9.2l3.309 2.481c5.169-5.097 8.1-7.053 10.586-8.548"
			></path>
		</g>
	</svg>
)

const CrossIcon = (
	<svg
		style={{
			display: "block",
			height: "1.25rem",
			width: "1.25rem",
			color: "#fd3838",
		}}
		viewBox="0 0 24 24"
	>
		<g fill="none" fillRule="evenodd">
			<path d="M24 0v24H0V0zM12.593 23.258l-.011.002l-.071.035l-.02.004l-.014-.004l-.071-.035c-.01-.004-.019-.001-.024.005l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427c-.002-.01-.009-.017-.017-.018m.265-.113l-.013.002l-.185.093l-.01.01l-.003.011l.018.43l.005.012l.008.007l.201.093c.012.004.023 0 .029-.008l.004-.014l-.034-.614c-.003-.012-.01-.02-.02-.022m-.715.002a.023.023 0 0 0-.027.006l-.006.014l-.034.614c0 .012.007.02.017.024l.015-.002l.201-.093l.01-.008l.004-.011l.017-.43l-.003-.012l-.01-.01z"></path>
			<path
				fill="currentColor"
				d="m12 14.122l5.303 5.303a1.5 1.5 0 0 0 2.122-2.122L14.12 12l5.304-5.303a1.5 1.5 0 1 0-2.122-2.121L12 9.879L6.697 4.576a1.5 1.5 0 1 0-2.122 2.12L9.88 12l-5.304 5.304a1.5 1.5 0 1 0 2.122 2.12z"
			></path>
		</g>
	</svg>
)

const Feature = ({ children, icon }) => {
	return (
		<div style={{ display: "flex", gap: "0.5rem", alignItems: "start" }}>
			{icon}
			<span
				style={{
					flex: "1",
					fontSize: "0.85rem",
					fontWeight: "500",
					color: "#333",
					lineHeight: "1.25",
				}}
			>
				{children}
			</span>
		</div>
	)
}

const PricingDisplay = ({
	price,
	compareAtPrice,
	billingPeriod,
	yearlySavings,
	displayPerMonth,
	quantityBilledThisPeriod = null,
	pricingReady = true,
}) => {
	const { t } = useTranslation()

	if (!pricingReady) {
		return (
			<div
				style={{
					width: "100%",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: "0.5rem",
				}}
			>
				<div
					style={{
						width: "8rem",
						height: "2.2rem",
						borderRadius: "0.6rem",
						background: "#f0f0f0",
					}}
				/>
				<div
					style={{
						width: "10rem",
						height: "0.8rem",
						borderRadius: "0.4rem",
						background: "#f0f0f0",
					}}
				/>
			</div>
		)
	}

	return (
		<div
			style={{
				width: "100%",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
			}}
		>
			<div
				style={{
					display: "flex",
					alignItems: "end",
					marginBottom: "0.5rem",
					gap: "0.3rem",
				}}
			>
				{compareAtPrice > 0 ? (
					<div
						style={{
							fontSize: "1.2rem",
							fontWeight: "700",
							color: "#777",
							lineHeight: "1",
							textDecoration: "line-through",
						}}
					>
						${compareAtPrice}
					</div>
				) : null}

				<div
					translate="no"
					style={{
						fontSize: "2rem",
						fontWeight: "700",
						color: "#222",
						lineHeight: "1",
					}}
				>
					<span style={{ verticalAlign: "middle", lineHeight: "1" }}>
						$
					</span>
					<SlotCounter
						duration={0.5}
						value={
							typeof price === "number" ? price.toFixed(2) : price
						}
					/>
				</div>
			</div>

			<div
				style={{
					fontSize: "0.7rem",
					fontWeight: "500",
					color: "#444",
					lineHeight: "1",
					paddingBottom: "0.2rem",
					marginBottom: "0.5rem",
				}}
			>
				{displayPerMonth ? t("components.billing_bundles.month") : null}
				{billingPeriod !== "monthly" && ", "}
				{quantityBilledThisPeriod
					? `$${quantityBilledThisPeriod} `
					: null}
				{billingPeriod === "quarterly"
					? t("components.billing_bundles.billed_quarterly")
					: billingPeriod === "yearly"
						? t("components.billing_bundles.billed_yearly")
						: null}
			</div>

			{yearlySavings > 0 ? (
				<div
					style={{
						fontSize: "0.7rem",
						justifySelf: "center",
						background: "#ccffb3",
						boxShadow:
							"0 0 0 1px #72fd4e80,0 16px 8px #72fd4e03,0 12px 6px #72fd4e0a,0 4px 4px #72fd4e12,0 1.5px 3px #72fd4e14,inset 0 1px 2px #ffffff1f",
						color: "#215108",
						borderRadius: "1rem",
						padding: "0.1rem 0.6rem",
					}}
				>
					<Trans
						i18nKey="components.billing_bundles.save_yearly"
						values={{ yearlySavings: yearlySavings?.toFixed(2) }}
					/>
				</div>
			) : null}
		</div>
	)
}

export const BillingBundleItems = {
	basic: (
		<Tooltip
			content={
				<div
					style={{
						padding: "0.5rem",
						fontSize: "0.8rem",
					}}
				>
					<p>Product Importer</p>
				</div>
			}
		>
			<img
				src={KopyLogo}
				style={{
					width: "3rem",
					height: "3rem",
					objectFit: "cover",
					borderRadius: "0.5rem",
				}}
			/>
		</Tooltip>
	),
	ai: (
		<Tooltip
			content={
				<div
					style={{
						padding: "0.5rem",
						fontSize: "0.8rem",
					}}
				>
					<p>AI Writer</p>
				</div>
			}
		>
			<div
				className="shadow border"
				style={{
					padding: "0.5rem",
					borderRadius: "0.5rem",
					background: "#fff",
					color: "#222",
				}}
			>
				<svg
					style={{
						display: "block",
						height: "2rem",
						width: "2rem",
					}}
					viewBox="0 0 14 14"
				>
					<path
						fill="currentColor"
						fillRule="evenodd"
						d="M9.739.85c.183-.835 1.374-.84 1.564-.005l.01.04l.017.077c.22.934.975 1.648 1.922 1.812c.872.152.872 1.404 0 1.556a2.396 2.396 0 0 0-1.925 1.827l-.024.103c-.19.834-1.38.83-1.564-.007l-.02-.088a2.38 2.38 0 0 0-1.917-1.836c-.87-.152-.87-1.402 0-1.553A2.38 2.38 0 0 0 9.718.948l.014-.064zM8.658 6.398A1.294 1.294 0 0 0 7.625 5.4v7.215a2.998 2.998 0 0 0 5.204-.85a3.016 3.016 0 0 1-.778-.183c-.595-.224-1.242-.665-1.773-1.436a.625.625 0 0 1 1.03-.709c.382.555.82.839 1.183.975c.184.07.348.1.476.11l.023.002C13.61 9.85 14 8.546 14 7.457c0-.734-.177-1.458-.483-2.072l-.079.015c-.52.09-.934.484-1.051.998l-.024.102c-.448 1.967-3.255 1.955-3.686-.016zM6.375 4.615a1.963 1.963 0 0 1 0-2.127v-.52a2.618 2.618 0 0 0-4.612 1.698v.002c.039.39.198.68.382.89c.229.261.47.368.527.38a.625.625 0 1 1-.277 1.22c-.36-.083-.82-.355-1.19-.775a2.813 2.813 0 0 1-.383-.562C.312 5.526 0 6.483 0 7.457c0 .735.178 1.568.483 2.238c.08-.09.167-.172.26-.244a.625.625 0 1 1 .774.98c-.178.141-.364.444-.478.795a2.997 2.997 0 0 0 5.336 1.389v-3.67a2.69 2.69 0 0 1-1.405.585a.625.625 0 1 1-.139-1.243c.509-.056.879-.349 1.143-.734c.268-.39.384-.818.401-1.023z"
						clipRule="evenodd"
					></path>
				</svg>
			</div>
		</Tooltip>
	),
	pages: (
		<Tooltip
			content={
				<div
					style={{
						padding: "0.5rem",
						fontSize: "0.8rem",
					}}
				>
					<p>AI Page Builder</p>
				</div>
			}
		>
			<div
				className="shadow border"
				style={{
					padding: "0.5rem",
					borderRadius: "0.5rem",
					background: "#fff",
					color: "#222",
				}}
			>
				<svg
					style={{
						display: "block",
						width: "2rem",
						height: "2rem",
					}}
					viewBox="0 0 24 24"
				>
					<path
						fill="currentColor"
						d="M16.08 6.63c-.07-.1-.15-.19-.23-.27h-.01a2.85 2.85 0 0 0-1.11-.69l-1.38-.45c-.1-.04-.2-.11-.26-.2a.5.5 0 0 1-.1-.31a.5.5 0 0 1 .1-.31c.06-.09.15-.16.26-.2l1.38-.45c.42-.14.79-.38 1.1-.69c.29-.3.52-.67.67-1.07v-.03l.45-1.38c.04-.1.11-.2.2-.26a.5.5 0 0 1 .31-.1c.11 0 .22.04.31.1s.16.15.2.26l.45 1.38c.14.42.38.8.69 1.11c.31.32.69.55 1.11.69l1.38.45h.03c.1.04.19.11.26.2s.1.2.1.31a.5.5 0 0 1-.1.31c-.06.09-.15.16-.26.2l-1.38.45c-.42.14-.8.38-1.11.69c-.32.31-.55.69-.69 1.11l-.46 1.38v.03c-.04.09-.11.17-.19.23a.5.5 0 0 1-.31.1a.5.5 0 0 1-.31-.1a.52.52 0 0 1-.2-.26l-.45-1.38c-.1-.31-.25-.6-.45-.85m7.7 3.8l-.77-.24c-.24-.08-.45-.21-.62-.38c-.17-.18-.3-.39-.38-.62l-.25-.76a.33.33 0 0 0-.11-.15a.27.27 0 0 0-.34 0c-.05.04-.09.09-.11.15l-.25.76c-.07.23-.2.44-.37.61s-.38.3-.61.38l-.77.25c-.06.02-.11.06-.15.11a.27.27 0 0 0 0 .34c.04.05.09.09.15.11l.77.25c.23.07.45.21.62.38s.3.39.38.62l.25.76c.02.06.06.11.11.15a.27.27 0 0 0 .34 0c.05-.04.09-.09.11-.15l.25-.76c.08-.24.21-.45.38-.62c.18-.17.39-.3.62-.38l.77-.25c.06-.02.11-.06.15-.11s.05-.11.05-.17s-.01-.12-.05-.17s-.106-.08-.17-.11m-4.24 2.28l.26.8c.05.14.12.25.2.37v6.08c0 1.24-1.01 2.25-2.25 2.25H6.25C5.01 22.21 4 21.2 4 19.96V4.46c0-1.24 1.01-2.25 2.25-2.25h8.26c-.07.04-.15.08-.22.11l-1.42.46c-.4.15-.75.4-.99.75a2 2 0 0 0-.38 1.18c0 .43.13.84.37 1.18c.078.112.178.202.275.289l.045.041H7.75c-.41 0-.75.34-.75.75s.34.75.75.75h7.24l.035.058a1 1 0 0 1 .085.162l.46 1.41c.14.4.4.74.75.99c.35.24.76.37 1.18.37c0 .37.11.73.33 1.04s.52.53.91.67zm-11.79 5h8.5c.41 0 .75-.34.75-.75s-.34-.75-.75-.75h-8.5c-.41 0-.75.34-.75.75s.34.75.75.75m0-5h8.5c.41 0 .75-.34.75-.75s-.34-.75-.75-.75h-8.5c-.41 0-.75.34-.75.75s.34.75.75.75"
					></path>
				</svg>
			</div>
		</Tooltip>
	),
	protect: (
		<Tooltip
			content={
				<div
					style={{
						padding: "0.5rem",
						fontSize: "0.8rem",
					}}
				>
					<p>Protect Store from Competitors</p>
				</div>
			}
		>
			<div
				className="shadow"
				style={{
					padding: "0.5rem",
					borderRadius: "0.5rem",
					background: "#222",
					color: "#fff",
				}}
			>
				<svg
					style={{
						display: "block",
						height: "2rem",
						width: "2rem",
					}}
					viewBox="0 0 512 512"
				>
					<path
						fill="currentColor"
						d="M256 0c4.6 0 9.2 1 13.4 2.9l188.3 79.9c22 9.3 38.4 31 38.3 57.2c-.5 99.2-41.3 280.7-213.6 363.2c-16.7 8-36.1 8-52.8 0C57.3 420.7 16.5 239.2 16 140c-.1-26.2 16.3-47.9 38.3-57.2L242.7 2.9C246.8 1 251.4 0 256 0m0 66.8v378C394 378 431.1 230.1 432 141.4z"
					></path>
				</svg>
			</div>
		</Tooltip>
	),
}

const KopyBasicPlusFeatures = ({ showOnlyEssentialPlan }) => {
	const { t } = useTranslation()

	return (
		<>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.unlock_ai_on_unlimited_stores_only_pay_for_1",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t("components.billing_bundles.import_products_in_1-click")}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t("components.billing_bundles.edit_products_before_importing")}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				<Tooltip
					content={
						<div
							style={{
								padding: "0.5rem",
								maxWidth: "25rem",
								fontSize: "0.8rem",
								lineHeight: "1.3",
							}}
						>
							{t("components.billing_bundles.kopy_mcp_tooltip")}
						</div>
					}
				>
					<span style={{ textDecoration: "underline dotted" }}>
						{t("components.billing_bundles.kopy_mcp")}
					</span>
				</Tooltip>
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.translate_products_to_any_language",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.improve_products_description_with_ai",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				<Tooltip
					content={
						<div
							style={{
								padding: "0.5rem",
								maxWidth: "25rem",
								fontSize: "0.8rem",
								lineHeight: "1.3",
							}}
						>
							<p>
								{t(
									"components.billing_bundles.kopy_pages_tooltip",
								)}
							</p>
							<p
								style={{
									marginTop: "0.5rem",
									marginBottom: "0.5rem",
								}}
							>
								{t(
									"components.billing_bundles.kopy_pages_tooltip_2",
								)}
							</p>
							<Video
								isInsideTooltip={true}
								videoSrc={KOPY_PAGES_TOOLTIP_VIDEO_URL}
							/>
						</div>
					}
				>
					<span
						style={{
							textDecoration: "underline dotted",
						}}
					>
						{t("components.billing_bundles.kopy_pages")}
					</span>
					<svg
						style={{ width: "1rem", height: "1rem" }}
						viewBox="0 0 24 24"
					>
						<path
							fill="#222"
							d="M11.95 18q.525 0 .888-.363t.362-.887q0-.525-.362-.888t-.888-.362q-.525 0-.887.363t-.363.887q0 .525.363.888t.887.362m-.9-3.85h1.85q0-.825.188-1.3t1.062-1.3q.65-.65 1.025-1.238T15.55 8.9q0-1.4-1.025-2.15T12.1 6q-1.425 0-2.312.75T8.55 8.55l1.65.65q.125-.45.563-.975T12.1 7.7q.8 0 1.2.438t.4.962q0 .5-.3.938t-.75.812q-1.1.975-1.35 1.475t-.25 1.825M12 22q-2.075 0-3.9-.787t-3.175-2.138q-1.35-1.35-2.137-3.175T2 12q0-2.075.788-3.9t2.137-3.175q1.35-1.35 3.175-2.137T12 2q2.075 0 3.9.788t3.175 2.137q1.35 1.35 2.138 3.175T22 12q0 2.075-.788 3.9t-2.137 3.175q-1.35 1.35-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12q0-3.35-2.325-5.675T12 4Q8.65 4 6.325 6.325T4 12q0 3.35 2.325 5.675T12 20m0-8"
						></path>
					</svg>
				</Tooltip>
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.6_premium_shopify_themes_included",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				<Tooltip
					content={
						<div
							style={{
								padding: "0.5rem",
								maxWidth: "25rem",
								fontSize: "0.8rem",
								lineHeight: "1.3",
							}}
						>
							{t(
								"components.billing_bundles.1_per_additional_1_000_credits",
							)}
						</div>
					}
				>
					<span style={{ textDecoration: "underline dotted" }}>
						{t(
							"components.billing_bundles.5_000_credits_per_month_included",
						)}
					</span>
				</Tooltip>
			</Feature>
			<Feature icon={CheckmarkIcon}>
				<Tooltip
					content={
						<div
							style={{
								padding: "0.5rem",
								maxWidth: "25rem",
								fontSize: "0.8rem",
								lineHeight: "1.3",
							}}
						>
							{t(
								"components.billing_bundles.100_credits_per_additional_page",
							)}
						</div>
					}
				>
					<span style={{ textDecoration: "underline dotted" }}>
						{t(
							"components.billing_bundles.generate_50_product_pages_with_ai_per_month",
						)}
					</span>
				</Tooltip>
			</Feature>
			{!showOnlyEssentialPlan && (
				<Feature icon={CrossIcon}>
					{t(
						"components.billing_bundles.protect_your_store_from_competitors",
					)}
				</Feature>
			)}
			{!showOnlyEssentialPlan && (
				<Feature icon={CrossIcon}>
					{t(
						"components.billing_bundles.protect_your_store_from_spy_tools",
					)}
				</Feature>
			)}
			{showOnlyEssentialPlan && (
				<Feature icon={CheckmarkIcon}>
					{t(
						"components.billing_bundles.invite_unlimited_members_to_your_team",
					)}
				</Feature>
			)}
		</>
	)
}

const KopyBasicFeatures = ({ hasValidKopyPlusSubscription }) => {
	const { t } = useTranslation()

	return (
		<>
			<Feature icon={CheckmarkIcon}>
				{t("components.billing_bundles.import_products_in_1-click")}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t("components.billing_bundles.edit_products_before_importing")}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				<Tooltip
					content={
						<div
							style={{
								padding: "0.5rem",
								maxWidth: "25rem",
								fontSize: "0.8rem",
								lineHeight: "1.3",
							}}
						>
							{t("components.billing_bundles.kopy_mcp_tooltip")}
						</div>
					}
				>
					<span style={{ textDecoration: "underline dotted" }}>
						{t("components.billing_bundles.kopy_mcp")}
					</span>
				</Tooltip>
			</Feature>
			<Feature
				icon={hasValidKopyPlusSubscription ? CheckmarkIcon : CrossIcon}
			>
				{t(
					"components.billing_bundles.translate_products_to_any_language",
				)}
			</Feature>
			<Feature
				icon={hasValidKopyPlusSubscription ? CheckmarkIcon : CrossIcon}
			>
				{t(
					"components.billing_bundles.improve_products_description_with_ai",
				)}
			</Feature>
			<Feature
				icon={hasValidKopyPlusSubscription ? CheckmarkIcon : CrossIcon}
			>
				<Tooltip
					content={
						<div
							style={{
								padding: "0.5rem",
								maxWidth: "25rem",
								fontSize: "0.8rem",
								lineHeight: "1.3",
							}}
						>
							<p>
								{t(
									"components.billing_bundles.kopy_pages_tooltip",
								)}
							</p>
							<p
								style={{
									marginTop: "0.5rem",
									marginBottom: "0.5rem",
								}}
							>
								{t(
									"components.billing_bundles.kopy_pages_tooltip_2",
								)}
							</p>
							<Video
								isInsideTooltip={true}
								videoSrc={KOPY_PAGES_TOOLTIP_VIDEO_URL}
							/>
						</div>
					}
				>
					<span style={{ textDecoration: "underline dotted" }}>
						{t("components.billing_bundles.kopy_pages")}
					</span>
					<svg
						style={{ width: "1rem", height: "1rem" }}
						viewBox="0 0 24 24"
					>
						<path
							fill="#222"
							d="M11.95 18q.525 0 .888-.363t.362-.887q0-.525-.362-.888t-.888-.362q-.525 0-.887.363t-.363.887q0 .525.363.888t.887.362m-.9-3.85h1.85q0-.825.188-1.3t1.062-1.3q.65-.65 1.025-1.238T15.55 8.9q0-1.4-1.025-2.15T12.1 6q-1.425 0-2.312.75T8.55 8.55l1.65.65q.125-.45.563-.975T12.1 7.7q.8 0 1.2.438t.4.962q0 .5-.3.938t-.75.812q-1.1.975-1.35 1.475t-.25 1.825M12 22q-2.075 0-3.9-.787t-3.175-2.138q-1.35-1.35-2.137-3.175T2 12q0-2.075.788-3.9t2.137-3.175q1.35-1.35 3.175-2.137T12 2q2.075 0 3.9.788t3.175 2.137q1.35 1.35 2.138 3.175T22 12q0 2.075-.788 3.9t-2.137 3.175q-1.35 1.35-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12q0-3.35-2.325-5.675T12 4Q8.65 4 6.325 6.325T4 12q0 3.35 2.325 5.675T12 20m0-8"
						></path>
					</svg>
				</Tooltip>
			</Feature>
			{!hasValidKopyPlusSubscription && (
				<Feature icon={CrossIcon}>
					{t(
						"components.billing_bundles.unlock_ai_on_unlimited_stores_only_pay_for_1",
					)}
				</Feature>
			)}
			{!hasValidKopyPlusSubscription && (
				<Feature icon={CrossIcon}>
					{t(
						"components.billing_bundles.6_premium_shopify_themes_included",
					)}
				</Feature>
			)}
			<Feature icon={CrossIcon}>
				{t(
					"components.billing_bundles.protect_your_store_from_competitors",
				)}
			</Feature>
			<Feature icon={CrossIcon}>
				{t(
					"components.billing_bundles.protect_your_store_from_spy_tools",
				)}
			</Feature>
		</>
	)
}

export const KopyBasicPlusProtectFeatures = () => {
	const { t } = useTranslation()

	return (
		<>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.unlock_ai_on_unlimited_stores_only_pay_for_1",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t("components.billing_bundles.import_products_in_1-click")}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t("components.billing_bundles.edit_products_before_importing")}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				<Tooltip
					content={
						<div
							style={{
								padding: "0.5rem",
								maxWidth: "25rem",
								fontSize: "0.8rem",
								lineHeight: "1.3",
							}}
						>
							{t("components.billing_bundles.kopy_mcp_tooltip")}
						</div>
					}
				>
					<span style={{ textDecoration: "underline dotted" }}>
						{t("components.billing_bundles.kopy_mcp")}
					</span>
				</Tooltip>
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.translate_products_to_any_language",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.improve_products_description_with_ai",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				<Tooltip
					content={
						<div
							style={{
								padding: "0.5rem",
								maxWidth: "25rem",
								fontSize: "0.8rem",
								lineHeight: "1.3",
							}}
						>
							<p>
								{t(
									"components.billing_bundles.kopy_pages_tooltip",
								)}
							</p>
							<p
								style={{
									marginTop: "0.5rem",
									marginBottom: "0.5rem",
								}}
							>
								{t(
									"components.billing_bundles.kopy_pages_tooltip_2",
								)}
							</p>
							<Video
								isInsideTooltip={true}
								videoSrc={KOPY_PAGES_TOOLTIP_VIDEO_URL}
							/>
						</div>
					}
				>
					<span style={{ textDecoration: "underline dotted" }}>
						{t("components.billing_bundles.kopy_pages")}
					</span>
					<svg
						style={{ width: "1rem", height: "1rem" }}
						viewBox="0 0 24 24"
					>
						<path
							fill="#222"
							d="M11.95 18q.525 0 .888-.363t.362-.887q0-.525-.362-.888t-.888-.362q-.525 0-.887.363t-.363.887q0 .525.363.888t.887.362m-.9-3.85h1.85q0-.825.188-1.3t1.062-1.3q.65-.65 1.025-1.238T15.55 8.9q0-1.4-1.025-2.15T12.1 6q-1.425 0-2.312.75T8.55 8.55l1.65.65q.125-.45.563-.975T12.1 7.7q.8 0 1.2.438t.4.962q0 .5-.3.938t-.75.812q-1.1.975-1.35 1.475t-.25 1.825M12 22q-2.075 0-3.9-.787t-3.175-2.138q-1.35-1.35-2.137-3.175T2 12q0-2.075.788-3.9t2.137-3.175q1.35-1.35 3.175-2.137T12 2q2.075 0 3.9.788t3.175 2.137q1.35 1.35 2.138 3.175T22 12q0 2.075-.788 3.9t-2.137 3.175q-1.35 1.35-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12q0-3.35-2.325-5.675T12 4Q8.65 4 6.325 6.325T4 12q0 3.35 2.325 5.675T12 20m0-8"
						></path>
					</svg>
				</Tooltip>
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.6_premium_shopify_themes_included",
				)}
			</Feature>

			<Feature icon={CheckmarkIcon}>
				<Tooltip
					content={
						<div
							style={{
								padding: "0.5rem",
								maxWidth: "25rem",
								fontSize: "0.8rem",
								lineHeight: "1.3",
							}}
						>
							{t(
								"components.billing_bundles.1_per_additional_1_000_credits",
							)}
						</div>
					}
				>
					<span style={{ textDecoration: "underline dotted" }}>
						{t(
							"components.billing_bundles.5_000_credits_per_month_included",
						)}
					</span>
				</Tooltip>
			</Feature>
			<Feature icon={CheckmarkIcon}>
				<Tooltip
					content={
						<div
							style={{
								padding: "0.5rem",
								maxWidth: "25rem",
								fontSize: "0.8rem",
								lineHeight: "1.3",
							}}
						>
							{t(
								"components.billing_bundles.100_credits_per_additional_page",
							)}
						</div>
					}
				>
					<span style={{ textDecoration: "underline dotted" }}>
						{t(
							"components.billing_bundles.generate_50_product_pages_with_ai_per_month",
						)}
					</span>
				</Tooltip>
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.protect_your_store_from_competitors",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.protect_your_store_from_spy_tools",
				)}
			</Feature>
		</>
	)
}

const KopyBasicProtectFeatures = () => {
	const { t } = useTranslation()

	return (
		<>
			<Feature icon={CheckmarkIcon}>
				{t("components.billing_bundles.import_products_in_1-click")}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t("components.billing_bundles.edit_products_before_importing")}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				<Tooltip
					content={
						<div
							style={{
								padding: "0.5rem",
								maxWidth: "25rem",
								fontSize: "0.8rem",
								lineHeight: "1.3",
							}}
						>
							{t("components.billing_bundles.kopy_mcp_tooltip")}
						</div>
					}
				>
					<span style={{ textDecoration: "underline dotted" }}>
						{t("components.billing_bundles.kopy_mcp")}
					</span>
				</Tooltip>
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.translate_products_to_any_language",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.improve_products_description_with_ai",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				<Tooltip
					content={
						<div
							style={{
								padding: "0.5rem",
								maxWidth: "25rem",
								fontSize: "0.8rem",
								lineHeight: "1.3",
							}}
						>
							<p>
								{t(
									"components.billing_bundles.kopy_pages_tooltip",
								)}
							</p>
							<p
								style={{
									marginTop: "0.5rem",
									marginBottom: "0.5rem",
								}}
							>
								{t(
									"components.billing_bundles.kopy_pages_tooltip_2",
								)}
							</p>
							<Video
								isInsideTooltip={true}
								videoSrc={KOPY_PAGES_TOOLTIP_VIDEO_URL}
							/>
						</div>
					}
				>
					<span style={{ textDecoration: "underline dotted" }}>
						{t("components.billing_bundles.kopy_pages")}
					</span>
					<svg
						style={{ width: "1rem", height: "1rem" }}
						viewBox="0 0 24 24"
					>
						<path
							fill="#222"
							d="M11.95 18q.525 0 .888-.363t.362-.887q0-.525-.362-.888t-.888-.362q-.525 0-.887.363t-.363.887q0 .525.363.888t.887.362m-.9-3.85h1.85q0-.825.188-1.3t1.062-1.3q.65-.65 1.025-1.238T15.55 8.9q0-1.4-1.025-2.15T12.1 6q-1.425 0-2.312.75T8.55 8.55l1.65.65q.125-.45.563-.975T12.1 7.7q.8 0 1.2.438t.4.962q0 .5-.3.938t-.75.812q-1.1.975-1.35 1.475t-.25 1.825M12 22q-2.075 0-3.9-.787t-3.175-2.138q-1.35-1.35-2.137-3.175T2 12q0-2.075.788-3.9t2.137-3.175q1.35-1.35 3.175-2.137T12 2q2.075 0 3.9.788t3.175 2.137q1.35 1.35 2.138 3.175T22 12q0 2.075-.788 3.9t-2.137 3.175q-1.35 1.35-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12q0-3.35-2.325-5.675T12 4Q8.65 4 6.325 6.325T4 12q0 3.35 2.325 5.675T12 20m0-8"
						></path>
					</svg>
				</Tooltip>
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.6_premium_shopify_themes_included",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.protect_your_store_from_competitors",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.protect_your_store_from_spy_tools",
				)}
			</Feature>
		</>
	)
}

const KopyProtectFeatures = () => {
	const { t } = useTranslation()

	return (
		<>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.protect_your_store_from_competitors",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.protect_your_store_from_spy_tools",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.kick_people_with_spy_tools_from_your_store",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t("components.billing_bundles.disables_inspect_element")}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.block_users_from_different_countries",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.redirect_customers_based_on_their_country_or_ip_address",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.blocks_keyboard_shortcuts_that_copy_content",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.prevents_your_images_from_being_copied",
				)}
			</Feature>
			<Feature icon={CheckmarkIcon}>
				{t(
					"components.billing_bundles.prevents_text_from_being_selected_and_copied",
				)}
			</Feature>
		</>
	)
}

export const BundleCard = ({
	planName,
	planDescription,
	planItems,
	planFeaturesComponent,
	onSelected,
	hasRightToTrial,
	price,
	compareAtPrice,
	billingPeriod,
	yearlySavings,
	glowingCTA,
	CTAText = null,
	displayPerMonth = true,
	quantityBilledThisPeriod = null,
	displayTrialingNotice = false,
	pricingReady = true,
}) => {
	const { t } = useTranslation()

	return (
		<div
			className="border shadow"
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				padding: "1rem",
				background: "#fff",
				borderRadius: "1rem",
				gap: "1rem",
				height: "100%",
			}}
		>
			<h1
				style={{
					fontSize: "1.5rem",
					fontWeight: "800",
					color: "#222",
					lineHeight: "1",
				}}
			>
				{planName}
			</h1>

			<div
				style={{ display: "flex", gap: "0.3rem", alignItems: "center" }}
			>
				{planItems[0]}

				{planItems.slice(1).map((item, index) => (
					<div
						key={index}
						style={{
							display: "flex",
							gap: "0.3rem",
							alignItems: "center",
						}}
					>
						<div style={{ fontSize: "1.5rem", fontWeight: "500" }}>
							+
						</div>
						<div>{item}</div>
					</div>
				))}
			</div>

			<h2
				style={{
					fontSize: "0.8rem",
					fontWeight: "500",
					color: "#444",
					lineHeight: "1",
				}}
			>
				{planDescription}
			</h2>

			<PricingDisplay
				price={price}
				compareAtPrice={compareAtPrice}
				billingPeriod={billingPeriod}
				yearlySavings={yearlySavings}
				displayPerMonth={displayPerMonth}
				quantityBilledThisPeriod={quantityBilledThisPeriod}
				pricingReady={pricingReady}
			/>

			<div
				style={{
					marginTop: "0.5rem",
					marginBottom: "0.5rem",
					display: "flex",
					flexDirection: "column",
					gap: "0.3rem",
				}}
			>
				{planFeaturesComponent}
			</div>

			<div style={{ marginTop: "auto", width: "100%" }}>
				<CoolButton
					onClick={onSelected}
					colored={glowingCTA}
					disabled={!pricingReady}
				>
					<span
						style={{
							fontSize: "0.9rem",
							fontWeight: "600",
							color: glowingCTA ? "#fff" : "#222",
						}}
					>
						{CTAText
							? CTAText
							: hasRightToTrial
								? t(
										"components.billing_bundles.start_3-day_free_trial",
									)
								: t("components.billing_bundles.buy_now")}
					</span>
				</CoolButton>

				{displayTrialingNotice && (
					<p
						style={{
							marginTop: "0.3rem",
							fontSize: "0.65rem",
							fontWeight: "500",
							color: "#777",
							lineHeight: "1",
							textAlign: "center",
						}}
					>
						{t("subscription.when_your_trial_ends")}
					</p>
				)}
			</div>
		</div>
	)
}

export const BillingBundles = ({
	onBundleSelected,
	hasRightToTrial,
	billingPeriod,
	useNewUserDiscounts,
	showOnlyEssentialPlan,
	showOnlyStarter = false,
}) => {
	const { state } = useContext(GlobalStateContext)
	const calculateItemCost = useCalculateItemCost()
	const pricingReady = useFeatureFlagsReady()
	const { t } = useTranslation()
	const isMobile = useMediaQuery("(max-width: 700px)")

	const [basicPrice, setBasicPrice] = useState("")
	const [basicPlusPrice, setBasicPlusPrice] = useState("")
	const [basicPlusProtectPrice, setBasicPlusProtectPrice] = useState("")
	const [basicProtectPrice, setBasicProtectPrice] = useState("")
	const [protectPrice, setProtectPrice] = useState("")

	const [basicYearlySavings, setBasicYearlySavings] = useState(0)
	const [basicPlusYearlySavings, setBasicPlusYearlySavings] = useState(0)
	const [basicPlusProtectYearlySavings, setBasicPlusProtectYearlySavings] =
		useState(0)
	const [basicProtectYearlySavings, setBasicProtectYearlySavings] =
		useState(0)
	const [protectYearlySavings, setProtectYearlySavings] = useState(0)

	const [basicCompareAtPrice, setBasicCompareAtPrice] = useState(0)
	const [basicPlusCompareAtPrice, setBasicPlusCompareAtPrice] = useState(0)
	const [basicPlusProtectCompareAtPrice, setBasicPlusProtectCompareAtPrice] =
		useState(0)
	const [basicProtectCompareAtPrice, setBasicProtectCompareAtPrice] =
		useState(0)
	const [protectCompareAtPrice, setProtectCompareAtPrice] = useState(0)

	const calculateMonthly = (total, billingPeriod) => {
		if (billingPeriod === "monthly") {
			return total
		} else if (billingPeriod === "yearly") {
			return total / 12
		} else if (billingPeriod === "quarterly") {
			return total / 3
		}
	}

	const calculatePrices = () => {
		/* prettier-ignore */
		const priceDiscountFactor =
			useNewUserDiscounts ?
				1 - (state.discounts?.essential_new_users?.[billingPeriod]?.discountValuePercentage || 0) / 100
			: 1

		let basicPrice = roundPrice(
			calculateMonthly(
				calculateItemCost("basic", billingPeriod, true),
				billingPeriod,
			) * priceDiscountFactor,
			0.99,
		)
		let basicPlusPrice = roundPrice(
			calculateMonthly(
				calculateItemCost("basic", billingPeriod, true) +
					calculateItemCost("plus", billingPeriod, true),
				billingPeriod,
			) * priceDiscountFactor,
			0.99,
		)
		let basicPlusProtectPrice = roundPrice(
			calculateMonthly(
				calculateItemCost("basic", billingPeriod, true) +
					calculateItemCost("plus", billingPeriod, true) +
					calculateItemCost("protect", billingPeriod, true),
				billingPeriod,
			) * priceDiscountFactor,
			0.99,
		)
		let basicProtectPrice = roundPrice(
			calculateMonthly(
				calculateItemCost("basic", billingPeriod, true) +
					calculateItemCost("protect", billingPeriod, true),
				billingPeriod,
			) * priceDiscountFactor,
			0.99,
		)
		let protectPrice = roundPrice(
			calculateMonthly(
				calculateItemCost("protect", billingPeriod, true),
				billingPeriod,
			) * priceDiscountFactor,
			0.99,
		)

		try {
			setBasicPrice(basicPrice)
			setBasicPlusPrice(basicPlusPrice)
			setBasicPlusProtectPrice(basicPlusProtectPrice)
			setBasicProtectPrice(basicProtectPrice)
			setProtectPrice(protectPrice)
		} catch (error) {
			console.error(error)
		}
	}

	const calculateYearlySavings = () => {
		if (billingPeriod === "monthly" || !useNewUserDiscounts) {
			setBasicYearlySavings(0)
			setBasicPlusYearlySavings(0)
			setBasicPlusProtectYearlySavings(0)
			setBasicProtectYearlySavings(0)
			setProtectYearlySavings(0)
			return
		}

		/* prettier-ignore */
		const priceDiscountFactor =
			useNewUserDiscounts ?
				1 - (state.discounts?.essential_new_users?.[billingPeriod]?.discountValuePercentage || 0) / 100
			: 1

		const basicPlusMonthlyPrice =
			calculateItemCost("basic", "monthly", true) +
			calculateItemCost("plus", "monthly", true)
		const basicPlusMonthlyPriceExtrapolatedYearly =
			basicPlusMonthlyPrice * 12
		const basicPlusPriceSelected =
			billingPeriod === "yearly"
				? calculateItemCost("basic", "yearly", true) +
					calculateItemCost("plus", "yearly", true)
				: calculateItemCost("basic", "quarterly", true) +
					calculateItemCost("plus", "quarterly", true)

		const basicPlusPriceSelectedExtrapolatedYearly =
			billingPeriod === "quarterly"
				? basicPlusPriceSelected * 4
				: basicPlusPriceSelected

		const basicPlusDifference = Math.round(
			basicPlusMonthlyPriceExtrapolatedYearly -
				basicPlusPriceSelectedExtrapolatedYearly * priceDiscountFactor,
		)

		setBasicPlusYearlySavings(basicPlusDifference)
	}

	const calculateCompareAtPrices = () => {
		if (billingPeriod === "monthly" || !useNewUserDiscounts) {
			setBasicCompareAtPrice(0)
			setBasicPlusCompareAtPrice(0)
			setBasicPlusProtectCompareAtPrice(0)
			setBasicProtectCompareAtPrice(0)
			setProtectCompareAtPrice(0)
			return
		}

		let basicCompareAtPrice = roundPrice(
			calculateMonthly(
				calculateItemCost("basic", billingPeriod, true),
				billingPeriod,
			),
			0.99,
		)

		let basicPlusCompareAtPrice = roundPrice(
			calculateMonthly(
				calculateItemCost("basic", billingPeriod, true) +
					calculateItemCost("plus", billingPeriod, true),
				billingPeriod,
			),
			0.99,
		)
		let basicPlusProtectCompareAtPrice = roundPrice(
			calculateMonthly(
				calculateItemCost("basic", billingPeriod, true) +
					calculateItemCost("plus", billingPeriod, true) +
					calculateItemCost("protect", billingPeriod, true),
				billingPeriod,
			),
			0.99,
		)
		let basicProtectCompareAtPrice = roundPrice(
			calculateMonthly(
				calculateItemCost("basic", billingPeriod, true) +
					calculateItemCost("protect", billingPeriod, true),
				billingPeriod,
			),
			0.99,
		)
		let protectCompareAtPrice = roundPrice(
			calculateMonthly(
				calculateItemCost("protect", billingPeriod, true),
				billingPeriod,
			),
			0.99,
		)

		setBasicCompareAtPrice(basicCompareAtPrice)
		setBasicPlusCompareAtPrice(basicPlusCompareAtPrice)
		setBasicPlusProtectCompareAtPrice(basicPlusProtectCompareAtPrice)
		setBasicProtectCompareAtPrice(basicProtectCompareAtPrice)
		setProtectCompareAtPrice(protectCompareAtPrice)
	}

	useEffect(() => {
		calculatePrices()
		calculateYearlySavings()
		calculateCompareAtPrices()
	}, [billingPeriod, calculateItemCost])

	// Cheaper yearly/quarterly additional-stores experiment (test arm):
	// bundles removed, only the Starter plan is offered.
	if (showOnlyStarter) {
		return (
			<div style={{ marginTop: "2rem", marginBottom: "2rem" }}>
				<div
					style={{
						width: isMobile ? "fit-content" : "56%",
						marginLeft: "auto",
						marginRight: "auto",
					}}
				>
					<BundleCard
						planName={t("components.billing_bundles.starter")}
						planDescription={t(
							"components.billing_bundles.kopy_basic",
						)}
						planItems={[BillingBundleItems.basic]}
						planFeaturesComponent={
							<KopyBasicFeatures
								hasValidKopyPlusSubscription={
									state.hasValidKopyPlusSubscription
								}
							/>
						}
						onSelected={() => onBundleSelected("basic")}
						hasRightToTrial={hasRightToTrial}
						price={basicPrice}
						compareAtPrice={basicCompareAtPrice}
						yearlySavings={basicYearlySavings}
						billingPeriod={billingPeriod}
						glowingCTA={true}
						pricingReady={pricingReady}
					/>
				</div>
			</div>
		)
	}

	return (
		<div style={{ marginTop: "2rem", marginBottom: "2rem" }}>
			<div
				style={{
					width: isMobile ? "fit-content" : "56%",
					marginLeft: "auto",
					marginRight: "auto",
					position: "relative",
					marginBottom: "1rem",
				}}
			>
				{showOnlyEssentialPlan ||
				!state.hasValidKopyPlusSubscription ? (
					<BundleCard
						planName={t("components.billing_bundles.essential")}
						planDescription={t(
							"components.billing_bundles.kopy_basic_ai",
						)}
						planItems={[
							BillingBundleItems.basic,
							BillingBundleItems.ai,
							BillingBundleItems.pages,
						]}
						planFeaturesComponent={
							<KopyBasicPlusFeatures
								showOnlyEssentialPlan={showOnlyEssentialPlan}
							/>
						}
						onSelected={() => onBundleSelected("basic_plus")}
						hasRightToTrial={hasRightToTrial}
						price={basicPlusPrice}
						compareAtPrice={basicPlusCompareAtPrice}
						yearlySavings={basicPlusYearlySavings}
						billingPeriod={billingPeriod}
						glowingCTA={true}
						pricingReady={pricingReady}
					/>
				) : (
					<BundleCard
						planName={t("components.billing_bundles.essential")}
						planDescription={t(
							"components.billing_bundles.kopy_basic_protect",
						)}
						planItems={[
							BillingBundleItems.basic,
							BillingBundleItems.protect,
						]}
						planFeaturesComponent={<KopyBasicProtectFeatures />}
						onSelected={() => onBundleSelected("basic_protect")}
						hasRightToTrial={hasRightToTrial}
						price={basicProtectPrice}
						compareAtPrice={basicProtectCompareAtPrice}
						yearlySavings={basicProtectYearlySavings}
						billingPeriod={billingPeriod}
						glowingCTA={true}
						pricingReady={pricingReady}
					/>
				)}
			</div>

			{!showOnlyEssentialPlan && (
				<div
					style={{
						display: "flex",
						gap: "1rem",
						alignItems: "stretch",
						justifyContent: "center",
						flexWrap: isMobile ? "wrap" : "nowrap",
					}}
				>
					<div style={{ width: isMobile ? "fit-content" : "50%" }}>
						<BundleCard
							planName={t("components.billing_bundles.starter")}
							planDescription={t(
								"components.billing_bundles.kopy_basic",
							)}
							planItems={[BillingBundleItems.basic]}
							planFeaturesComponent={
								<KopyBasicFeatures
									hasValidKopyPlusSubscription={
										state.hasValidKopyPlusSubscription
									}
								/>
							}
							onSelected={() => onBundleSelected("basic")}
							hasRightToTrial={hasRightToTrial}
							price={basicPrice}
							compareAtPrice={basicCompareAtPrice}
							yearlySavings={basicYearlySavings}
							billingPeriod={billingPeriod}
							glowingCTA={false}
							pricingReady={pricingReady}
						/>
					</div>
					<div style={{ width: isMobile ? "fit-content" : "50%" }}>
						{state.hasValidKopyPlusSubscription ? (
							<BundleCard
								planName={t(
									"components.billing_bundles.protect",
								)}
								planDescription={t(
									"components.billing_bundles.kopy_protect",
								)}
								planItems={[BillingBundleItems.protect]}
								planFeaturesComponent={<KopyProtectFeatures />}
								onSelected={() => onBundleSelected("protect")}
								hasRightToTrial={hasRightToTrial}
								price={protectPrice}
								compareAtPrice={protectCompareAtPrice}
								yearlySavings={protectYearlySavings}
								billingPeriod={billingPeriod}
								glowingCTA={false}
								pricingReady={pricingReady}
							/>
						) : (
							<BundleCard
								planName={t(
									"components.billing_bundles.ultimate",
								)}
								planDescription={t(
									"components.billing_bundles.kopy_basic_ai_protect",
								)}
								planItems={[
									BillingBundleItems.basic,
									BillingBundleItems.ai,
									BillingBundleItems.pages,
									BillingBundleItems.protect,
								]}
								planFeaturesComponent={
									<KopyBasicPlusProtectFeatures />
								}
								onSelected={() =>
									onBundleSelected("basic_plus_protect")
								}
								hasRightToTrial={hasRightToTrial}
								price={basicPlusProtectPrice}
								compareAtPrice={basicPlusProtectCompareAtPrice}
								yearlySavings={basicPlusProtectYearlySavings}
								billingPeriod={billingPeriod}
								glowingCTA={false}
								pricingReady={pricingReady}
							/>
						)}
					</div>
				</div>
			)}
		</div>
	)
}
